import { useQuery } from "@tanstack/react-query";
import { usePublicClient } from "wagmi";
import { Address, parseAbiItem } from "viem";
import { supabaseExtended } from "@/integrations/supabase/extendedClient";

interface Holder {
  address: string;
  percentage: string;
}

interface HolderDistributionProps {
  tokenAddress: string;
}

const TRANSFER_EVENT = parseAbiItem("event Transfer(address indexed from, address indexed to, uint256 value)");
const ERC20_READ_ABI = [
  parseAbiItem("function balanceOf(address account) view returns (uint256)"),
  parseAbiItem("function totalSupply() view returns (uint256)"),
];
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

const fetchHolderDistribution = async (tokenAddress: string, publicClient: any): Promise<Holder[]> => {
  if (!publicClient || !/^0x[a-fA-F0-9]{40}$/.test(tokenAddress)) return [];
  const latest = await publicClient.getBlockNumber();
  let fromBlock = latest > 100000n ? latest - 100000n : 0n;
  try {
    const { data: rows } = await (supabaseExtended as any).from("created_coins").select("transaction_hash").eq("contract_address", tokenAddress).limit(1);
    const transactionHash = rows?.[0]?.transaction_hash;
    if (transactionHash) {
      const receipt = await publicClient.getTransactionReceipt({ hash: transactionHash as `0x${string}` });
      fromBlock = receipt.blockNumber;
    }
  } catch {
    // Continue with a bounded recent-history scan when deployment metadata is unavailable.
  }
  const addresses = new Set<string>();
  const chunkSize = 10_000n;

  for (let start = fromBlock; start <= latest; start += chunkSize) {
    const end = start + chunkSize - 1n > latest ? latest : start + chunkSize - 1n;
    const logs = await publicClient.getLogs({ address: tokenAddress as Address, event: TRANSFER_EVENT, fromBlock: start, toBlock: end });
    logs.forEach((log: any) => {
      const from = log.args?.from?.toLowerCase();
      const to = log.args?.to?.toLowerCase();
      if (from && from !== ZERO_ADDRESS) addresses.add(from);
      if (to && to !== ZERO_ADDRESS) addresses.add(to);
    });
  }

  const totalSupply = await publicClient.readContract({ address: tokenAddress as Address, abi: ERC20_READ_ABI, functionName: "totalSupply" }) as bigint;
  if (!totalSupply) return [];
  const balances = await Promise.all([...addresses].map(async (address) => ({
    address,
    balance: await publicClient.readContract({ address: tokenAddress as Address, abi: ERC20_READ_ABI, functionName: "balanceOf", args: [address as Address] }).catch(() => 0n),
  })));

  return balances
    .filter(({ balance }) => balance > 0n)
    .sort((a, b) => Number(b.balance > a.balance ? 1 : b.balance < a.balance ? -1 : 0))
    .slice(0, 5)
    .map(({ address, balance }) => ({
      address: `${address.slice(0, 6)}...${address.slice(-4)}`,
      percentage: `${(Number((balance * 10000n) / totalSupply) / 100).toFixed(2)}%`,
    }));
};

const HolderDistribution = ({ tokenAddress }: HolderDistributionProps) => {
  const publicClient = usePublicClient({ chainId: 137 });
  const { data: holders, isLoading, error } = useQuery({
    queryKey: ["holders", tokenAddress],
    queryFn: () => fetchHolderDistribution(tokenAddress, publicClient),
    enabled: Boolean(tokenAddress && publicClient),
    staleTime: 60_000,
  });

  if (isLoading) {
    return <div className="mt-4"><h4 className="text-sm font-medium mb-2">holder distribution</h4><div className="text-sm text-muted-foreground">Loading live Polygon holder data…</div></div>;
  }

  if (error || !holders?.length) {
    return <div className="mt-4"><h4 className="text-sm font-medium mb-2">holder distribution</h4><div className="text-sm text-muted-foreground">No holder data available yet.</div></div>;
  }

  return (
    <div className="mt-4">
      <h4 className="text-sm font-medium mb-2">holder distribution</h4>
      <div className="space-y-1">
        {holders.map((holder, index) => <div key={holder.address} className="flex justify-between text-sm"><span className="text-muted-foreground">{index + 1}. {holder.address}</span><span>{holder.percentage}</span></div>)}
      </div>
    </div>
  );
};

export default HolderDistribution;
